import React from "react";
import _ from "lodash";
import {
  AWAITING_RESPONDANT,
  RESPONDANT_ONBOARDED,
  NEGOTIATION_REACHED,
  NEGOTIATION_QUIT_BY_CLAIMENT,
  NEGOTIATION_QUIT_BY_RESPONDENT,
  CLAIMENT_PAID_NEGOTIATION,
  BOTH_PAID_NEGOTIATION,
  RESPONDENT_PAID_NEGOTIATION,
  NEGOTIATION_QUIT_BY_CASEMANAGER,
} from "../../../../helpers/constants";

import CommentBox from "../common/CommentBox";
import CaseAgreement from "../common/CaseAgreement";
import AgreementDocCard from "../common/AgreementDocCard";
import { Spacing } from "../styles";

/**
 * Renders CommentBox with standard props.
 */
const RenderCommentBox = ({
  expand = true,
  status,
  errorText,
  comments,
  caseDetails,
  setComments,
  disabled,
  isLocalTranscriptionServer,
  isRespondent,
  currentState,
  ownerId,
  partyList,
}) => (
  <CommentBox
    title={`${_.capitalize(caseDetails?.resolutionKind)} Process`}
    expand={expand}
    status={status}
    errorText={errorText}
    ownerId={ownerId}
    caseId={caseDetails?.id}
    comments={comments}
    disabled={disabled}
    setComments={setComments}
    isRespondent={isRespondent}
    hasCaseManager={!_.isEmpty(caseDetails?.caseManager)}
    hasMediator={
      caseDetails?.requestedMediators?.length &&
      caseDetails?.requestedMediators?.some((el) => el?.id)
    }
    hideAddComment={HIDE_COMMENT_KEYS.includes(currentState?.key)}
    partyList={partyList}
    isLocalTranscriptionServer={isLocalTranscriptionServer}
    kind={
      caseDetails?.resolutionKind === "mediation" ? "Mediator" : "Arbitrator"
    }
  />
);

/**
 * Separate static list of keys where comment input is hidden.
 */
const HIDE_COMMENT_KEYS = [
  "awaitingRespondentAcceptance",
  "noticeToArbitrate",
  "arbitrationReferenced",
  "arbitrationNominated",
  "arbitrationConfirmed",
  "waitingForArbitratorConfirmation",
  "firstNoticeSent",
  "statementOfClaimFiled",
  "awaitingForSec17Details",
  "sec17PetitionFiled",
  "sec17Ordered",
  "awaitingSec17Execution",
  "sec17ExtensionMemoFiled",
  "sec17ExtensionMemoOrdered",
  "fsOrdered",
  "attachmentOrdered",
  "garnisheeProhibitaryOrder",
  "garnisheeFinalOrder",
  "sec17Closed",
  "statementOfDefenceFiled",
  "rejoinderFromClaimant",
  "surrejoinderFromRespondent",
  "2ndNoticeMOM",
  "evidenceFiledByClaimant",
  "evidenceFiledByRespondent",
  "crossExamination",
  "argumentsByClaimant",
  "argumentsByRespondent",
  "negotiationOngoing",
  "firstHearing",
  "secondHearing",
  "thirdHearing",
  "firstMom",
  "arbitrationInprogress",
  "commencementLetter",
  "referenceLetter",
];

/**
 * Grouped for case-specific rendering logic.
 */
const STATE_CONFIG = {
  [AWAITING_RESPONDANT]: { errorText: "Respondent Declined the Case" },
  [RESPONDANT_ONBOARDED]: { errorText: "Respondent Declined the Case" },
  [NEGOTIATION_QUIT_BY_CLAIMENT]: {
    status: "error",
    errorText: "Case Quit by Claimant",
    disabled: true,
  },
  [NEGOTIATION_QUIT_BY_RESPONDENT]: {
    status: "error",
    errorText: "Case Quit by Respondent",
    disabled: true,
  },
  [NEGOTIATION_QUIT_BY_CASEMANAGER]: {
    status: "error",
    errorText: "Case Closed by Scrutiny Clerk",
    disabled: true,
  },
  [NEGOTIATION_REACHED]: { disabled: true },
  [CLAIMENT_PAID_NEGOTIATION]: { disabled: true, showAgreement: true },
  [RESPONDENT_PAID_NEGOTIATION]: { disabled: true, showAgreement: true },
  [BOTH_PAID_NEGOTIATION]: { disabled: true, showAgreement: true },
  negotiationOngoing: {},
  finalAward: { disabled: true, showAgreement: true },
  reservedForAward: { showAgreement: true },
  awardPassed: { showAgreement: true },
  settledAndWithdrawn: { showAgreement: true },
  updatedAndWithdrawn: { showAgreement: true },
  resolutionFailed: {},
};

/**
 * Reusable Agreement Section
 */
const AgreementSection = ({ caseDetails, currentState, isRespondent }) => (
  <>
    <Spacing>
      <CaseAgreement
        title={`Final Award Summary for Case ID: ${caseDetails?.id}`}
        message={caseDetails?.negotiationSummary}
        summaryStatus={caseDetails?.summaryStatus === "completed"}
        isPaid={currentState?.cta?.length === 0}
      />
    </Spacing>
    <Spacing>
      <AgreementDocCard
        id={caseDetails?.id}
        status={caseDetails.status}
        isRespondent={isRespondent}
        hasRating={caseDetails?.rating}
        hasAgreement={caseDetails?.hasAgreementDocument}
        type="Download"
      />
    </Spacing>
  </>
);

function MediationState({
  currentState,
  isRespondent,
  ownerId,
  caseDetails,
  comments,
  setComments,
  partyList,
  isLocalTranscriptionServer,
}) {
  const key = currentState?.key;
  const config = STATE_CONFIG[key] || {};

  // If the key doesn’t exist, return null
  if (!key) return null;

  return (
    <>
      <Spacing>
        <RenderCommentBox
          expand={config.expand ?? true}
          status={config.status}
          errorText={config.errorText}
          disabled={config.disabled}
          comments={comments}
          setComments={setComments}
          caseDetails={caseDetails}
          isRespondent={isRespondent}
          isLocalTranscriptionServer={isLocalTranscriptionServer}
          currentState={currentState}
          ownerId={ownerId}
          partyList={partyList}
        />
      </Spacing>
      {config.showAgreement && (
        <AgreementSection
          caseDetails={caseDetails}
          currentState={currentState}
          isRespondent={isRespondent}
        />
      )}
    </>
  );
}

export default React.memo(MediationState);
